/**
 * useJobProgress
 *
 * Reads a single tracked job from the job-progress store by `job_id`, and
 * reconciles via REST when we hold an id but have no live snapshot yet (mounted
 * after the first event, or recovering from a dropped socket). The always-on
 * Echo bridge is the primary source; this hook is a thin reactive view.
 */

import { useEffect, useMemo } from 'react';

import {
  JOB_PROGRESS_CONSTANTS,
  JobProgressUtils,
  jobProgressService,
  type JobProgress,
  type JobStatus,
} from '@personal-trainer-app/shared';

import { useJobProgressStore } from '@/stores';

export interface UseJobProgressReturn {
  /** The latest snapshot, or null when nothing is tracked for this id. */
  progress: JobProgress | null;
  /** Job status, or `'idle'` when untracked. */
  status: JobStatus | 'idle';
  /** 0–100. */
  percentage: number;
  message: string | null;
  metadata: Record<string, unknown>;
  isTerminal: boolean;
  isDone: boolean;
  /** A real failure — a job held for review is not one. */
  isFailed: boolean;
  /** Ended waiting on a person: a plan held for an admin's review. */
  isHeld: boolean;
}

const IDLE_VIEW: UseJobProgressReturn = {
  progress: null,
  status: 'idle',
  percentage: 0,
  message: null,
  metadata: {},
  isTerminal: false,
  isDone: false,
  isFailed: false,
  isHeld: false,
};

/** Derive the public view from a raw snapshot. Shared by the by-type hook. */
export function deriveJobProgressView(
  progress: JobProgress | null
): UseJobProgressReturn {
  if (!progress) return IDLE_VIEW;
  return {
    progress,
    status: progress.status,
    percentage: progress.percentage,
    message: progress.message,
    metadata: progress.metadata,
    isTerminal: JobProgressUtils.isTerminalJobStatus(progress.status),
    isDone: progress.status === 'completed',
    isFailed: JobProgressUtils.isFailedJob(progress),
    isHeld: JobProgressUtils.isHeldJob(progress),
  };
}

export const useJobProgress = (
  jobId: string | null | undefined
): UseJobProgressReturn => {
  const progress = useJobProgressStore((state) =>
    jobId ? (state.jobs[jobId] ?? null) : null
  );

  useEffect(() => {
    if (!JOB_PROGRESS_CONSTANTS.LIVE_JOB_PROGRESS || !jobId) return;
    // Already have a live snapshot — no need to reconcile.
    if (useJobProgressStore.getState().jobs[jobId]) return;

    let cancelled = false;
    void jobProgressService.getProgress(jobId).then((snapshot) => {
      if (!cancelled && snapshot) {
        useJobProgressStore.getState().upsert(snapshot);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [jobId]);

  return useMemo(() => deriveJobProgressView(progress), [progress]);
};

/** All non-terminal jobs currently tracked — e.g. for a header "N running" chip. */
export const useActiveJobs = (): JobProgress[] => {
  const jobs = useJobProgressStore((state) => state.jobs);
  return useMemo(
    () =>
      Object.values(jobs).filter(
        (job) => !JobProgressUtils.isTerminalJobStatus(job.status)
      ),
    [jobs]
  );
};
